import React from "react";
import { Input } from "antd";
import { useRef, useEffect, useCallback } from "react";

import type { InputRef } from "antd";

export interface TypeAddressParam {
  /**
   * @param street 街道地址
   */
  street: string;
  /**
   * @param state 州
   */
  state: string;
  /**
   * @param county 县/行政区
   */
  county: string;
  /**
   * @param city 城市
   */
  city: string;
  /**
   * @param zip 邮政编码
   */
  zip: string;
  /**
   * @param address 完整的地址，包含了某个位置的所有必要信息
   */
  address: string;
}

export interface TypeAddressSelectProps {
  value?: string;
  onChange?(address?: string): void;
  /**
   * @name onAddress 用户选择的地址详细对象
   * @param {TypeAddressParam} address 地址详情
   */
  onAddress?(address?: TypeAddressParam | undefined): void;
  /**
   * @param fullAddress onChange方法是否传递完整的路径
   * @default false
   * @description onChange方法与Form表单状态自动绑定，间接管理该组件字段关联的Form value
   * - true  : 232 Forsyth Street Southwest, Atlanta, GA 30303-3702
   * - false : 232 Forsyth Street Southwest
   */
  fullAddress?: boolean;
  /**
   * @param disabled 是否可输入
   */
  disabled?: boolean;
  /**
   * @param placeholder 输入框占位符
   */
  placeholder?: string;
  /**
   * @param allowClear 是否开启清空图标
   */
  allowClear?: boolean;
}

function parseGoogleAddress(list): TypeAddressParam {
  const map: Record<string, string> = {};
  list.forEach((c) => c.types.forEach((type) => (map[type] = c.long_name)));
  const mapShort: Record<string, string> = {};
  list.forEach((c) =>
    c.types.forEach((type) => (mapShort[type] = c.short_name)),
  );

  let zip = map["postal_code"] || "";
  const zipSuffix = mapShort["postal_code_suffix"] || "";
  if (zipSuffix) zip = `${zip}-${zipSuffix}`;

  const street = [map["street_number"], map["route"]].filter(Boolean).join(" ");
  const city =
    map["locality"] || map["sublocality"] || map["neighborhood"] || "";
  const stateShort = mapShort["administrative_area_level_1"] || "";

  const address = `${street}, ${city}, ${stateShort} ${zip}`;

  return {
    zip,
    city,
    state: map["administrative_area_level_1"] || "",
    county: map["administrative_area_level_2"] || "",
    street,
    address,
  };
}

/**
 * @name parseAddress 解析地址
 * @param address 2350 Mission College Boulevard, Santa Clara, CA 95054
 * @description 通过Google的API解析的，但是也有可能传入的地址不规范，导致失败，返回空对象
 */
export async function parseAddress(
  address: string,
): Promise<Partial<Omit<TypeAddressParam, "street" | "address">>> {
  try {
    const geocoder = new window.google.maps.Geocoder();
    // 直接返回的是一个 Promise 对象
    const { results } = await geocoder.geocode({ address });
    if (results?.length) {
      const comp = results[0].address_components;
      const get = (t) => comp.find((c) => c.types.includes(t))?.long_name || "";
      const data = {
        state: get("administrative_area_level_1"),
        county: get("administrative_area_level_2"),
        city: get("locality"),
        zip: get("postal_code"),
      };
      return data;
    }
  } catch (e) {
    console.error("@-parseAddress-e", e);
    return {};
  }
}

parseAddress("2350 Mission College Boulevard, Santa Clara, CA 95054");

/**
 * @name AddressSelect 地址查询选择
 */
const AddressSelect: React.FC<TypeAddressSelectProps> = ({
  value,
  disabled,
  onChange,
  onAddress,
  allowClear,
  fullAddress,
  placeholder = "Enter street address",
}) => {
  const ref = useRef<InputRef>();

  const refGoogleAPI = useRef<Record<string, Function>>({});

  const onSelect = useCallback(() => {
    const place = refGoogleAPI.current.getPlace();
    if (!place?.address_components?.length) return;
    const address = parseGoogleAddress(place.address_components);
    onChange?.(fullAddress ? address.address : address.street);
    onAddress?.(address);
  }, []);

  function onClear() {
    onAddress?.();
    onChange?.();
  }

  useEffect(() => {
    if (!window?.google?.maps?.places?.Autocomplete) return;
    refGoogleAPI.current = new window.google.maps.places.Autocomplete(
      ref.current.input,
      {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["address_components", "formatted_address"],
      },
    );
    refGoogleAPI.current.addListener("place_changed", onSelect);
  }, [onSelect]);

  return (
    <Input
      ref={ref}
      value={value}
      onClear={onClear}
      disabled={disabled}
      placeholder={placeholder}
      className="address-select-input"
      style={{ height: 44 }}
      allowClear={allowClear}
      onChange={(e) => onChange?.(e.target.value)}
    />
  );
};

export default AddressSelect;
